import React, { useState, useEffect, useCallback } from "react";
import { ExtraFeeWrapper } from "./Styled";
import Title from "./Title";
import BackButton from "../../../shared/components/BackButton";
import useMergeState from "../../../shared/hooks/useMergeState";
import FormContainer from "./FormContainer";
import { api } from "../../../shared/hooks/api";
import { error, success } from "../../../utils/toast";
import { useNavigate } from "react-router-dom";
import CancelModal from "../../../shared/components/CancelModal";
import { WarningUnsavedChanges } from "../../../configs/warningProductModal";

const initialValue = {
    price: 0,
};

const CreateExtraFee = () => {
    const [validated, setValidated] = useState(false);
    const [extraFee, mergeExtraFee] = useMergeState(initialValue);
    const navigate = useNavigate();
    const [{ isCreating }, createExtraFee] = api.post("/extrafees/");
    const [isModified, setIsModified] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [navigationPath, setNavigationPath] = useState("");

    // Mark as modified when extraFee changes
    useEffect(() => {
        if (extraFee.name || extraFee.price !== 0) {
            setIsModified(true);
        }
    }, [extraFee]);

    // Add beforeunload event listener for browser navigation
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isModified) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);

    const confirmNavigation = useCallback((path: string) => {
        if (isModified) {
            setNavigationPath(path);
            setShowUnsavedDialog(true);
            return false;
        }
        
        navigate(path);
        return true;
    }, [isModified, navigate]);

    const handleBackClick = () => {
        confirmNavigation("/extrafees/");
    };

    const handleSaveAndNavigate = async () => {
        setShowUnsavedDialog(false);
        await handleSubmit();
    };

    const handleDontSaveAndNavigate = () => {
        setShowUnsavedDialog(false);
        navigate(navigationPath);
    };

    const handleContinueEditing = () => {
        setShowUnsavedDialog(false);
    };

    const handleSubmit = () => {
        return createExtraFee(extraFee)
            .then((resp: any) => {
                success("Thành Công", "Tạo mới dịch vụ thành công");
                setIsModified(false);
                navigate(`/extrafees/`);
                return true;
            })
            .catch((err: any) => {
                error("Lỗi tạo mới dịch vụ", err);
                return false;
            });
    };

    const handleChange = (updates: any) => {
        setIsModified(true);
        mergeExtraFee(updates);
    };

    return (
        <ExtraFeeWrapper>
            <BackButton label="Trở về" onClick={handleBackClick} />
            <Title
                title="Thông tin chi phí"
                setValidated={setValidated}
                submitForm={handleSubmit}
                isLoading={isCreating}
            />
            <FormContainer
                validated={validated}
                extraFee={extraFee}
                mergeExtraFee={handleChange}
            />

            <CancelModal
                open={showUnsavedDialog}
                handleClose={handleDontSaveAndNavigate}
                handleConfirm={handleSaveAndNavigate}
                handleCancel={handleContinueEditing}
                title={WarningUnsavedChanges.title}
                contentText={WarningUnsavedChanges.context}
                closeButtonText={WarningUnsavedChanges.closeButtonText}
                confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                showCancelButton={true}
            />
        </ExtraFeeWrapper>
    );
};

export default CreateExtraFee;
